if (!customElements.get('thumbnail-slider')) {
  customElements.define('thumbnail-slider', class ThumbnailSlider extends HTMLElement {
    constructor() {
      super();
      this.slider = this.querySelector('.thumbnail-list');
      this.sliderItems = this.querySelectorAll('.thumbnail-list__item');
      // Get buttons specifically from the thumbnail slider buttons div
      this.buttons = this.querySelector('.slider-buttons')?.querySelectorAll('.slider-button') || [];
      this.bindEvents();
    }
 
    bindEvents() {
      if (this.buttons.length) {
        this.buttons.forEach(button =>
          button.addEventListener('click', this.onButtonClick.bind(this))
        );
      }
    }
 
    onButtonClick(event) {
      event.preventDefault();
      event.stopPropagation();
 
      const button = event.currentTarget;
      const isNext = button.name === 'next';
      const step = parseInt(button.dataset.step || 3);
      const isDesktop = window.matchMedia('(min-width: 750px)').matches;
      const isVerticalLayout = isDesktop && this.closest('.product--thumbnail_slider');
     
      const thumbnailList = this.slider;
      if (!thumbnailList || !this.sliderItems.length) return;
 
      // Calculate scroll amount based on thumbnail size and step
      const firstItem = this.sliderItems[0];
      const itemSize = isVerticalLayout ? firstItem.offsetHeight : firstItem.offsetWidth;
      const scrollAmount = itemSize * step;
 
      // Get current scroll position
      const currentScroll = isVerticalLayout ? thumbnailList.scrollTop : thumbnailList.scrollLeft;
     
      // Calculate new scroll position
      const newScroll = currentScroll + (isNext ? scrollAmount : -scrollAmount);
 
      // Calculate max scroll
      const containerSize = isVerticalLayout ? thumbnailList.clientHeight : thumbnailList.clientWidth;
      const totalSize = isVerticalLayout ? thumbnailList.scrollHeight : thumbnailList.scrollWidth;
      const maxScroll = Math.max(0, totalSize - containerSize);
 
      // Apply scroll with bounds checking
      const finalScroll = Math.max(0, Math.min(newScroll, maxScroll));
 
      if (isVerticalLayout) {
        thumbnailList.scrollTo({
          top: finalScroll,
          behavior: 'smooth'
        });
      } else {
        thumbnailList.scrollTo({
          left: finalScroll,
          behavior: 'smooth'
        });
      }
 
      // Update button states
      this.updateButtonStates(finalScroll, maxScroll);
    }
 
    updateButtonStates(currentScroll, maxScroll) {
      if (!this.buttons.length) return;
 
      const prevButton = Array.from(this.buttons).find(button => button.name === 'previous');
      const nextButton = Array.from(this.buttons).find(button => button.name === 'next');
 
      if (prevButton) {
        prevButton.disabled = currentScroll <= 0;
      }
      if (nextButton) {
        nextButton.disabled = currentScroll >= maxScroll;
      }
    }
  });
}
 
if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        pagination: this.querySelector('.slider-pagination'),
        // Get buttons specifically from the main gallery slider buttons div
        sliderButtons: this.querySelector('.product__media-gallery .slider-buttons')?.querySelectorAll('.slider-button') || []
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      if (!this.elements.thumbnails) return;
 
      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
     
      // Setup thumbnail click handlers
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        const button = mediaToSwitch.querySelector('button');
        if (button) {
          button.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.setActiveMedia(mediaToSwitch.dataset.target, false, false);
          });
        }
      });
 
      // Setup main gallery slider buttons
      if (this.elements.sliderButtons.length) {
        this.elements.sliderButtons.forEach(button => {
          button.addEventListener('click', this.onSliderButtonClick.bind(this));
        });
      }
     
      if (this.elements.pagination) {
        this.elements.pagination.querySelectorAll('.pagination-dot').forEach((dot) => {
          dot.addEventListener('click', this.onPaginationDotClick.bind(this));
        });
      }
     
      if (this.dataset.desktopLayout === 'thumbnail_slider' && this.mql.matches) this.removeListSemantic();
    }
 
    onSlideChanged(event) {
      if (!event.detail.currentElement) return;
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${event.detail.currentElement.dataset.mediaId}"]`);
      this.setActiveThumbnail(thumbnail);
      this.updatePaginationDots(event.detail.currentElement);
    }
 
    setActiveMedia(mediaId, prepend, scrollIntoView = false) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
      if (!activeMedia) return;
 
      // Update media states
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');
 
      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          if (activeThumbnail) {
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }
 
      this.preventStickyHeader();
 
      // Handle mobile scrolling
      if (!this.mql.matches) {
        const mediaList = activeMedia.closest('.product__media-list');
        if (mediaList) {
          const allMediaItems = Array.from(mediaList.children);
          const activeIndex = allMediaItems.indexOf(activeMedia);
          if (activeIndex !== -1) {
            mediaList.scrollTo({
              left: activeMedia.offsetLeft,
              behavior: 'smooth'
            });
            // Update pagination dots for mobile
            this.updatePaginationDots(activeMedia);
            // Update slider counter if it exists
            this.updateSliderCounter(activeIndex + 1);
          }
        }
      }
 
      this.playActiveMedia(activeMedia);
 
      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
      if (activeThumbnail) {
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }
    }
 
    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;
 
      this.elements.thumbnails.querySelectorAll('[id^="Slide-"]').forEach((element) => {
        element.classList.remove('is-active');
        element.querySelector('button')?.removeAttribute('aria-current');
      });
     
      thumbnail.classList.add('is-active');
      thumbnail.querySelector('button')?.setAttribute('aria-current', true);
 
      // Scroll thumbnail into view without affecting page scroll
      const isDesktop = this.mql.matches;
      const isVerticalLayout = isDesktop && this.closest('.product--thumbnail_slider');
      const thumbnailList = thumbnail.closest('.thumbnail-list');
     
      if (!thumbnailList) return;
 
      if (isVerticalLayout) {
        const containerHeight = thumbnailList.clientHeight;
        const thumbnailTop = thumbnail.offsetTop;
        const thumbnailHeight = thumbnail.clientHeight;
        const scrollTop = thumbnailList.scrollTop;
       
        if (thumbnailTop < scrollTop || thumbnailTop + thumbnailHeight > scrollTop + containerHeight) {
          thumbnailList.scrollTo({
            top: thumbnailTop - (containerHeight - thumbnailHeight) / 2,
            behavior: 'smooth'
          });
        }
      } else {
        const containerWidth = thumbnailList.clientWidth;
        const thumbnailLeft = thumbnail.offsetLeft;
        const thumbnailWidth = thumbnail.clientWidth;
        const scrollLeft = thumbnailList.scrollLeft;
       
        if (thumbnailLeft < scrollLeft || thumbnailLeft + thumbnailWidth > scrollLeft + containerWidth) {
          thumbnailList.scrollTo({
            left: thumbnailLeft - (containerWidth - thumbnailWidth) / 2,
            behavior: 'smooth'
          });
        }
      }
    }
 
    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = theme.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }
 
    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }
 
    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }
 
    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
 
    onPaginationDotClick(event) {
      const dot = event.currentTarget;
      const slideIndex = parseInt(dot.dataset.slideIndex);
      const slides = this.elements.viewer.querySelectorAll('.product__media-item');
     
      if (slides[slideIndex]) {
        const mediaId = slides[slideIndex].dataset.mediaId;
        this.setActiveMedia(mediaId, false, true);
      }
    }
 
    onSliderButtonClick(event) {
      event.preventDefault();
      event.stopPropagation();
 
      const button = event.currentTarget;
      const isNext = button.name === 'next';
      const mediaList = this.elements.viewer.querySelector('.product__media-list');
      if (!mediaList) return;
 
      const mediaItems = mediaList.querySelectorAll('.product__media-item');
      const activeItem = mediaList.querySelector('.is-active') || mediaItems[0];
      if (!activeItem) return;
 
      const currentIndex = Array.from(mediaItems).indexOf(activeItem);
      const newIndex = isNext ? currentIndex + 1 : currentIndex - 1;
 
      if (newIndex >= 0 && newIndex < mediaItems.length) {
        const targetMedia = mediaItems[newIndex];
        this.setActiveMedia(targetMedia.dataset.mediaId, false, true);
      }
    }
 
    updatePaginationDots(activeMedia) {
      if (!this.elements.pagination) return;
     
      const dots = this.elements.pagination.querySelectorAll('.pagination-dot');
      if (!dots.length) return;
 
      const mediaList = activeMedia.closest('.product__media-list');
      if (!mediaList) return;
 
      const allMediaItems = Array.from(mediaList.children);
      const activeIndex = allMediaItems.indexOf(activeMedia);
     
      dots.forEach((dot, index) => {
        dot.classList.toggle('is-active', index === activeIndex);
        dot.setAttribute('aria-current', index === activeIndex ? 'true' : 'false');
      });
    }
 
    updateSliderCounter(currentIndex) {
      const counter = this.querySelector('.slider-counter--current');
      if (counter) {
        counter.textContent = currentIndex;
      }
    }
  });
}
 
 